import React, { useEffect, useState } from "react";
import { apiClient } from "../../../services/apiService";
import { OpenApiDiffRenderer } from "./DiffViewer";
import { diffOpenApiSpecs } from "./diffCalculator";

interface Props {
  repo?: string;
  activeTab?: string;
}

const VersionComparison = ({ repo, activeTab }: Props) => {
  const [repos, setRepos] = useState<string[]>([]);
  const [selectedRepo, setSelectedRepo] = useState(repo || "");
  const [versions, setVersions] = useState<any[]>([]);
  const [baseVersionId, setBaseVersionId] = useState("");
  const [compareVersionId, setCompareVersionId] = useState("");
  const [differences, setDifferences] = useState<any | null>(null);

  useEffect(() => {
    apiClient.get("/api-contract/api-list").then((res) => setRepos(res.data));
  }, []);

  useEffect(() => {
    if (selectedRepo) {
      apiClient
        .get(`/api-contract/file/${selectedRepo}/versions`)
        .then((res) => {
          const sorted = res.data.versions.sort(
            (a: any, b: any) =>
              new Date(b.uploadedAt).getTime() -
              new Date(a.uploadedAt).getTime()
          );
          setVersions(sorted);
          if (sorted.length >= 2) {
            setBaseVersionId(sorted[1].versionId);
            setCompareVersionId(sorted[0].versionId);
          }
        });
    }
  }, [selectedRepo, activeTab]);

  useEffect(() => {
    if (selectedRepo && baseVersionId && compareVersionId) {
      Promise.all([
        apiClient.get(
          `/api-contract/file/${selectedRepo}/versions/${baseVersionId}/openapi`
        ),
        apiClient.get(
          `/api-contract/file/${selectedRepo}/versions/${compareVersionId}/openapi`
        ),
      ]).then(([baseRes, compareRes]) => {
        const baseSpec = baseRes.data;
        const compareSpec = compareRes.data;
        setDifferences(diffOpenApiSpecs(baseSpec, compareSpec));
      });
    }
  }, [baseVersionId, compareVersionId]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select API
          </label>
          <select
            value={selectedRepo}
            onChange={(e) => setSelectedRepo(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">-- Select API --</option>
            {repos.map((repo) => (
              <option key={repo} value={repo}>
                {repo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Base Version
          </label>
          <select
            value={baseVersionId}
            onChange={(e) => setBaseVersionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            {versions.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.versionId}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Compare Version
          </label>
          <select
            value={compareVersionId}
            onChange={(e) => setCompareVersionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            {versions.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.versionId}
              </option>
            ))}
          </select>
        </div>
      </div>

      {differences ? (
        <OpenApiDiffRenderer diffData={differences} />
      ) : baseVersionId === compareVersionId ? (
        <div className="text-gray-500 text-center mt-6">
          <p className="text-sm">At least two versions are needed to compare</p>
        </div>
      ) : null}
    </div>
  );
};

export default VersionComparison;
